import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { App, Button, Divider, Flex, Grid, Input, Skeleton, Tabs, Typography } from 'antd';
import {
    TbActivity,
    TbCalendarEvent,
    TbCircleCheck,
    TbCopy,
    TbDots,
    TbExternalLink,
    TbLayoutDashboard,
    TbLink,
    TbMessages,
    TbPaperclip,
    TbProgressCheck,
    TbTrash,
    TbX,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { taskService } from '@/services';
import { TaskUpdateInput } from '@/services/database/tasks';
import {
    canCommentOnTask,
    canEditTask,
    canManageTaskStatuses,
    MiddleDot,
    PlatformDatePicker,
    PreviousNextResourceButtonGroup,
    PriorityDropdown,
    ResourceActivityFeed,
    ResourceAssociations,
    ResourceCommentsPanel,
    ResourceHeader,
    ResourceHeaderTags,
    ResourceHeaderTitle,
    ResourceVisibilityDropdown,
    RichTextField,
    SmallTooltip,
    subscribeResourceActivityOpenEvents,
    TaskParticipantPicker,
    TaskPriorityIcon,
    TaskStatusDropdown,
    WorkspaceResourceActionsDropdown,
    WorkspaceResourceGlyph,
    type WorkspaceResourceActionMenuItem,
} from '@/shared';
import ResourceAttachmentsPanel, {
    AttachmentDocumentState,
} from '@/shared/components/attachments/ResourceAttachmentsPanel';
import EmptySection from '@/shared/components/layouts/views/EmptySection';
import { useResourceRoutePreview } from '@/shared/components/resource-route-preview/context';
import ResourcePreviewField from '@/shared/components/resource-route-preview/ResourcePreviewField';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useMemberStore, useUserStore } from '@/stores';
import {
    Task,
    TaskAssigneeStatusType,
    TaskParticipantRole,
    TaskPriority,
    TaskStatus,
    TaskVisibility,
    WorkspaceSubjectType,
} from '@/types';
import { dayjs, formatDate } from '@/utilities';

import TaskPropertyRow from './components/TaskPropertyRow';
import TaskDetailFrame from './components/TaskDetailFrame';

import './index.css';

interface TaskDetailProps {
    onClose: () => void;
    onTaskNavigate: (taskId: string) => void;
    onTaskDeleted: (taskId: string) => void;
    onTaskUpdated: (task: Task) => void;
    open: boolean;
    taskId: string | null;
    tasks: Task[];
}

type TaskDetailTab = 'activity' | 'attachments' | 'comments' | 'overview';

const TaskDetail: FC<TaskDetailProps> = ({
    onClose,
    onTaskDeleted,
    onTaskNavigate,
    onTaskUpdated,
    open,
    taskId,
    tasks,
}): JSX.Element => {
    const { modal } = App.useApp();
    const routePreview = useResourceRoutePreview();
    const historicalSnapshot =
        routePreview?.resourceType === 'tasks' && routePreview.resourceId === taskId
            ? routePreview.snapshot
            : undefined;
    const { token } = useAntDesignTheme();
    const screens = Grid.useBreakpoint();
    const selectedMember = useMemberStore((state) => state.selectedMember);
    const selectedUser = useUserStore((state) => state.selectedUser);
    const [description, setDescription] = useState('');
    const [activeTab, setActiveTab] = useState<TaskDetailTab>('overview');
    const [attachmentDocumentState, setAttachmentDocumentState] = useState<AttachmentDocumentState>();
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [priority, setPriority] = useState<TaskPriority | ''>('');
    const [saving, setSaving] = useState(false);
    const [task, setTask] = useState<Task | null>(null);
    const [title, setTitle] = useState('');
    const canEdit = Boolean(
        !routePreview && task && canEditTask(task, selectedMember?.id, selectedUser?.id, selectedMember?.permission),
    );
    const canComment = Boolean(
        !routePreview &&
        task &&
        canCommentOnTask(task, selectedMember?.id, selectedUser?.id, selectedMember?.permission),
    );
    const attachmentDestination = useMemo(
        () => ({
            listName: 'Tasks',
            recordName: task?.title ?? 'Task',
            resourceId: task?.id ?? '',
            resourceName: task?.title ?? 'Task',
            resourceType: 'task' as const,
        }),
        [task?.id, task?.title],
    );

    useEffect(() => {
        let active = true;

        if (!open || !taskId) {
            setTask(null);
            setError(false);
            return () => {
                active = false;
            };
        }

        const loadTask = async () => {
            try {
                setLoading(true);
                setError(false);
                const liveTask = await taskService.getById(taskId);
                const nextTask = { ...liveTask, ...historicalSnapshot, id: liveTask.id };

                if (active) {
                    setTask(nextTask);
                    setTitle(nextTask.title);
                    setDescription(nextTask.description ?? '');
                    setPriority(nextTask.priority ?? '');
                }
            } catch (loadError) {
                captureException(loadError);
                if (active) {
                    setError(true);
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        void loadTask();
        return () => {
            active = false;
        };
    }, [historicalSnapshot, open, taskId]);

    useEffect(() => {
        if (open) {
            setActiveTab('overview');
            setAttachmentDocumentState(undefined);
        }
    }, [open, taskId]);

    useEffect(
        () =>
            subscribeResourceActivityOpenEvents((detail) => {
                if (detail.resourceId !== taskId || detail.resourceType !== 'tasks' || !detail.tab) {
                    return;
                }

                setActiveTab(detail.tab);
            }),
        [taskId],
    );

    const handleTaskUpdated = useCallback(
        (updatedTask: Task) => {
            setTask(updatedTask);
            setTitle(updatedTask.title);
            setDescription(updatedTask.description ?? '');
            setPriority(updatedTask.priority ?? '');
            onTaskUpdated(updatedTask);
        },
        [onTaskUpdated],
    );
    const persistTask = async (updates: TaskUpdateInput) => {
        if (!task || !canEdit) {
            return;
        }

        try {
            setSaving(true);
            handleTaskUpdated(await taskService.update(task.id, updates));
        } catch (updateError) {
            captureException(updateError);
            setTitle(task.title);
            setDescription(task.description ?? '');
            toast.error('Unable to Update Task');
        } finally {
            setSaving(false);
        }
    };
    const setTaskStatus = async (status: TaskStatus) => {
        if (!task || !canEdit) {
            return;
        }

        try {
            setSaving(true);
            handleTaskUpdated(await taskService.setStatus(task.id, status.id));
        } catch (updateError) {
            captureException(updateError);
            toast.error('Unable to Update Task Status');
        } finally {
            setSaving(false);
        }
    };
    const handleToggleCompleted = async () => {
        if (!task || !canEdit) {
            return;
        }

        try {
            setSaving(true);
            const updatedTask = await taskService.setCompleted(task.id, !task.completed);
            handleTaskUpdated(updatedTask);
            toast(updatedTask.completed ? 'Task Marked Complete' : 'Task Marked Active', {
                description: updatedTask.title,
            });
        } catch (updateError) {
            captureException(updateError);
            toast.error('Unable to Update Task');
        } finally {
            setSaving(false);
        }
    };
    const handleDuplicate = async () => {
        if (!task || !canEdit) {
            return;
        }

        try {
            setSaving(true);
            const duplicatedTask = await taskService.duplicate(task.id);

            onTaskUpdated(duplicatedTask);
            onTaskNavigate(duplicatedTask.id);
            toast('Task Duplicated', { description: duplicatedTask.title });
        } catch (duplicateError) {
            captureException(duplicateError);
            toast.error('Unable to Duplicate Task');
        } finally {
            setSaving(false);
        }
    };
    const handleDelete = async () => {
        if (!task || !canEdit) {
            return;
        }

        try {
            setSaving(true);
            await taskService.delete(task.id);
            onTaskDeleted(task.id);
            onClose();
        } catch (deleteError) {
            captureException(deleteError);
            toast.error('Unable to Delete Task');
        } finally {
            setSaving(false);
        }
    };
    const handleTitleBlur = () => {
        const nextTitle = title.trim();

        if (!task || !nextTitle || nextTitle === task.title) {
            setTitle(task?.title ?? '');
            return;
        }

        void persistTask({ title: nextTitle });
    };
    const assigneeMemberIds =
        task?.assignees
            ?.filter(
                (participant) =>
                    participant.role === TaskParticipantRole.ASSIGNEE &&
                    participant.status === TaskAssigneeStatusType.ACTIVE,
            )
            .map((participant) => participant.member.id) ?? [];
    const reviewerMemberIds =
        task?.assignees
            ?.filter(
                (participant) =>
                    participant.role === TaskParticipantRole.REVIEWER &&
                    participant.status === TaskAssigneeStatusType.ACTIVE,
            )
            .map((participant) => participant.member.id) ?? [];
    const assigneeParticipants =
        task?.assignees?.filter(
            (participant) =>
                participant.role === TaskParticipantRole.ASSIGNEE &&
                participant.status === TaskAssigneeStatusType.ACTIVE,
        ) ?? [];
    const reviewerParticipants =
        task?.assignees?.filter(
            (participant) =>
                participant.role === TaskParticipantRole.REVIEWER &&
                participant.status === TaskAssigneeStatusType.ACTIVE,
        ) ?? [];
    const canManageStatuses = !routePreview && canManageTaskStatuses(selectedMember?.permission);

    const confirmDelete = () => {
        modal.confirm({
            cancelText: 'Cancel',
            content: 'This action cannot be undone.',
            okButtonProps: { danger: true },
            okText: 'Delete',
            onOk: handleDelete,
            title: 'Delete task?',
        });
    };
    const taskUrl = task
        ? `${window.location.origin}/relations/tasks?task=${encodeURIComponent(task.id)}`
        : window.location.href;
    const copyTaskLink = async () => {
        if (!task) {
            return;
        }

        try {
            await navigator.clipboard.writeText(taskUrl);
            toast('Link Copied', {
                icon: <TbLink size='1.125rem' style={{ marginRight: '0.5rem' }} />,
                description: task.title,
            });
        } catch (copyError) {
            captureException(copyError);
            toast.error('Unable to Copy Task Link');
        }
    };
    const taskActionItems: WorkspaceResourceActionMenuItem[] = task
        ? [
              {
                  disabled: saving,
                  icon: task.completed ? <TbProgressCheck size='1rem' /> : <TbCircleCheck size='1rem' />,
                  key: 'completed',
                  label: task.completed ? 'Mark Active' : 'Mark Complete',
                  onClick: () => void handleToggleCompleted(),
                  permission: 'update',
                  section: 'state',
              },
              {
                  icon: <TbLink size='1rem' />,
                  key: 'copyLink',
                  label: 'Copy Link',
                  onClick: () => void copyTaskLink(),
                  permission: 'read',
              },
              {
                  icon: <TbExternalLink size='1rem' />,
                  key: 'openInNewWindow',
                  label: 'Open in New Window',
                  onClick: () => window.open(taskUrl, '_blank', 'noopener,noreferrer'),
                  permission: 'read',
              },
              {
                  disabled: saving,
                  icon: <TbCopy size='1rem' />,
                  key: 'duplicate',
                  label: 'Duplicate',
                  onClick: () => void handleDuplicate(),
                  permission: 'update',
              },
              {
                  danger: true,
                  disabled: saving,
                  icon: <TbTrash size='1rem' />,
                  key: 'delete',
                  label: 'Delete',
                  onClick: confirmDelete,
                  permission: 'manage',
              },
          ]
        : [];

    const headerActions = (
        <>
            {task && (
                <PreviousNextResourceButtonGroup
                    enableHotkeys={false}
                    onNextResource={(nextTask: Task) => onTaskNavigate(nextTask.id)}
                    onPreviousResource={(previousTask: Task) => onTaskNavigate(previousTask.id)}
                    pluralResourceLabel='tasks'
                    resource={task}
                    resources={tasks}
                    showCount
                    singularResourceLabel='task'
                />
            )}
            <Divider type='vertical' />
            {task ? (
                <WorkspaceResourceActionsDropdown
                    access={{
                        manage: { disabled: !canEdit },
                        read: { disabled: false },
                        update: { disabled: !canEdit },
                    }}
                    history={{ resourceId: task.id, resourceType: 'tasks', title: task.title }}
                    items={taskActionItems}
                    resourceLabel='Task'
                >
                    <Button
                        aria-label='Task actions'
                        disabled={saving}
                        icon={<TbDots style={{ color: token.colorTextTertiary }} />}
                        size='small'
                        type='text'
                    />
                </WorkspaceResourceActionsDropdown>
            ) : (
                <Button
                    aria-label='Task actions'
                    disabled
                    icon={<TbDots style={{ color: token.colorTextTertiary }} />}
                    size='small'
                    type='text'
                />
            )}
            <Divider type='vertical' />
            <SmallTooltip title='Close' placement='bottomRight'>
                <Button
                    aria-label='Close task'
                    icon={<TbX style={{ color: token.colorTextTertiary }} />}
                    onClick={onClose}
                    size='small'
                />
            </SmallTooltip>
        </>
    );

    return (
        <TaskDetailFrame
            closable={false}
            destroyOnHidden
            mask
            maskClosable
            onClose={onClose}
            open={open}
            styles={{
                body: {
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    overflow: 'hidden',
                    padding: 0,
                },
                mask: { backgroundColor: 'transparent' },
                wrapper: { borderLeft: `1px solid ${token.colorBorder}` },
            }}
            width={screens.lg ? (attachmentDocumentState ? 'min(72rem, 100%)' : '48rem') : '100%'}
        >
            {!attachmentDocumentState ? (
                <ResourceHeader
                    breadcrumbs={[
                        {
                            icon: <WorkspaceResourceGlyph resource='task' />,
                            key: 'tasks',
                            label: 'Tasks',
                            to: '/relations/tasks',
                        },
                        {
                            key: 'task',
                            content: (
                                <ResourceHeaderTitle
                                    editable={{
                                        ariaLabel: 'Rename task',
                                        disabled: saving || !canEdit,
                                        onSave: (nextTitle) => persistTask({ title: nextTitle }),
                                    }}
                                    fallbackWidth={16 * 7}
                                    title={task?.title}
                                />
                            ),
                        },
                    ]}
                    leftActions={
                        task ? (
                            <ResourceHeaderTags
                                disabled={saving || !canEdit}
                                resourceLabel={task.title}
                                subject={{
                                    subjectId: task.id,
                                    subjectLabel: task.title,
                                    subjectType: WorkspaceSubjectType.TASK,
                                }}
                            />
                        ) : null
                    }
                    rightActions={routePreview ? null : headerActions}
                    style={{ flex: 'none', padding: '0.75rem 1rem' }}
                />
            ) : null}
            <div className='task-detail-content'>
                {loading ? (
                    <div style={{ padding: '1.5rem' }}>
                        <Skeleton active paragraph={{ rows: 12 }} />
                    </div>
                ) : error || !task ? (
                    <EmptySection
                        description='The task may have been removed, or you may no longer have access.'
                        height='100%'
                        icon={<WorkspaceResourceGlyph color={token.colorTextTertiary} resource='task' size='1.5rem' />}
                        label='Task Unavailable'
                    />
                ) : (
                    <Tabs
                        activeKey={activeTab}
                        className={`task-detail-tabs${attachmentDocumentState ? ' task-detail-tabs-document' : ''}`}
                        destroyOnHidden
                        onChange={(key) => setActiveTab(key as TaskDetailTab)}
                        size='small'
                        items={[
                            {
                                key: 'overview',
                                label: (
                                    <Flex align='center' gap='0.375rem'>
                                        <TbLayoutDashboard />
                                        Overview
                                    </Flex>
                                ),
                                children: (
                                    <main className='task-detail-overview'>
                                        <div className='task-detail-overview-scroll'>
                                            <section className='task-detail-properties'>
                                                <TaskPropertyRow label='Status' field='status'>
                                                    <TaskStatusDropdown
                                                        disabled={saving || !canEdit}
                                                        manageEnabled={canManageStatuses}
                                                        onChange={setTaskStatus}
                                                        value={task.status}
                                                    />
                                                </TaskPropertyRow>
                                                <TaskPropertyRow label='Priority' field='priority'>
                                                    <PriorityDropdown
                                                        onTaskUpdated={handleTaskUpdated}
                                                        priority={priority}
                                                        setPriority={setPriority}
                                                        task={task}
                                                    >
                                                        <Button disabled={saving || !canEdit} size='small'>
                                                            <TaskPriorityIcon priority={priority} />
                                                            {priority || 'No Priority'}
                                                        </Button>
                                                    </PriorityDropdown>
                                                </TaskPropertyRow>
                                                <TaskPropertyRow label='Due Date' field='dueAt'>
                                                    <PlatformDatePicker
                                                        allowClear
                                                        disabled={saving || !canEdit}
                                                        onChange={(value) =>
                                                            void persistTask({ dueAt: value?.toDate() ?? null })
                                                        }
                                                        placeholder='Due Date...'
                                                        size='small'
                                                        suffixIcon={<TbCalendarEvent />}
                                                        value={task.dueAt ? dayjs(task.dueAt) : null}
                                                    />
                                                </TaskPropertyRow>
                                                <TaskPropertyRow label='Assignees' field='assigneeMemberIds'>
                                                    <TaskParticipantPicker
                                                        disabled={saving || !canEdit}
                                                        label='Assignees'
                                                        onChange={(memberIds) =>
                                                            void persistTask({ assigneeMemberIds: memberIds })
                                                        }
                                                        participants={assigneeParticipants}
                                                        selectedMemberIds={assigneeMemberIds}
                                                        variant='assignee'
                                                    />
                                                </TaskPropertyRow>
                                                <TaskPropertyRow label='Reviewers' field='reviewerMemberIds'>
                                                    <TaskParticipantPicker
                                                        disabled={saving || !canEdit}
                                                        label='Reviewers'
                                                        onChange={(memberIds) =>
                                                            void persistTask({ reviewerMemberIds: memberIds })
                                                        }
                                                        participants={reviewerParticipants}
                                                        selectedMemberIds={reviewerMemberIds}
                                                        variant='reviewer'
                                                    />
                                                </TaskPropertyRow>
                                                <TaskPropertyRow label='Visibility' field='visibility'>
                                                    <ResourceVisibilityDropdown
                                                        disabled={saving || !canEdit}
                                                        options={Object.values(TaskVisibility)}
                                                        setVisibility={(visibility) =>
                                                            void persistTask({
                                                                visibility: visibility as TaskVisibility,
                                                            })
                                                        }
                                                        visibility={task.visibility ?? TaskVisibility.PRIVATE}
                                                    />
                                                </TaskPropertyRow>
                                            </section>
                                            <div className='task-detail-editor'>
                                                <Typography.Text type='secondary'>Title</Typography.Text>
                                                <ResourcePreviewField
                                                    field='title'
                                                    resourceType='tasks'
                                                    resourceId={task.id}
                                                >
                                                    <Input
                                                        disabled={saving || !canEdit}
                                                        maxLength={250}
                                                        onBlur={handleTitleBlur}
                                                        onChange={(event) => setTitle(event.target.value)}
                                                        onPressEnter={(event) => event.currentTarget.blur()}
                                                        style={{
                                                            fontWeight: 600,
                                                            marginBottom: '1rem',
                                                            marginTop: '0.5rem',
                                                        }}
                                                        value={title}
                                                    />
                                                </ResourcePreviewField>
                                                <Typography.Text type='secondary'>Description</Typography.Text>
                                                <div style={{ marginTop: '0.5rem' }}>
                                                    <ResourcePreviewField
                                                        field='description'
                                                        resourceType='tasks'
                                                        resourceId={task.id}
                                                    >
                                                        <RichTextField
                                                            disabled={saving || !canEdit}
                                                            minHeight='18rem'
                                                            onBlur={(nextDescription) => {
                                                                if (nextDescription !== (task.description ?? '')) {
                                                                    void persistTask({ description: nextDescription });
                                                                }
                                                            }}
                                                            onChange={setDescription}
                                                            value={description}
                                                        />
                                                    </ResourcePreviewField>
                                                </div>
                                            </div>
                                        </div>
                                        <Flex
                                            align='center'
                                            className='task-detail-footer'
                                            justify='space-between'
                                            style={{ borderTop: `1px solid ${token.colorBorder}` }}
                                        >
                                            <Flex align='center'>
                                                <ResourceAssociations
                                                    resourceId={task.id}
                                                    resourceType='tasks'
                                                    resourceUpdatedAt={task.updatedAt}
                                                />
                                                <MiddleDot horizontalMargin='0.5rem' />
                                                <small style={{ color: token.colorTextTertiary }}>
                                                    Updated {formatDate(task.updatedAt)}
                                                </small>
                                            </Flex>
                                            <small style={{ color: token.colorTextTertiary }}>
                                                Created {formatDate(task.createdAt)}
                                            </small>
                                        </Flex>
                                    </main>
                                ),
                            },
                            {
                                key: 'attachments',
                                label: (
                                    <Flex align='center' gap='0.375rem'>
                                        <TbPaperclip />
                                        Attachments
                                    </Flex>
                                ),
                                children: (
                                    <div style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
                                        <ResourceAttachmentsPanel
                                            canComment={canComment}
                                            canUpload={canEdit}
                                            destination={attachmentDestination}
                                            documentPresentation='inline'
                                            resourceBreadcrumbLabel='Tasks'
                                            resourceBreadcrumbPath='/relations/tasks'
                                            resourceLabel='Task'
                                            onDocumentStateChange={setAttachmentDocumentState}
                                            onResourceBreadcrumbNavigate={() => setActiveTab('overview')}
                                        />
                                    </div>
                                ),
                            },
                            {
                                key: 'comments',
                                label: (
                                    <Flex align='center' gap='0.375rem'>
                                        <TbMessages />
                                        Comments
                                    </Flex>
                                ),
                                children: (
                                    <div className='task-detail-panel'>
                                        <ResourceCommentsPanel
                                            canComment={canComment}
                                            height='100%'
                                            resourceId={task.id}
                                            resourceTitle={task.title}
                                            resourceType='tasks'
                                        />
                                    </div>
                                ),
                            },
                            {
                                key: 'activity',
                                label: (
                                    <Flex align='center' gap='0.375rem'>
                                        <TbActivity />
                                        Activity
                                    </Flex>
                                ),
                                children: (
                                    <div className='task-detail-panel'>
                                        <ResourceActivityFeed
                                            canExport={canEdit}
                                            height='100%'
                                            resourceId={task.id}
                                            resourceTitle={task.title}
                                            resourceType='tasks'
                                        />
                                    </div>
                                ),
                            },
                        ]}
                    />
                )}
            </div>
        </TaskDetailFrame>
    );
};

export default TaskDetail;
